import React, { useState, useMemo, useEffect } from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Icon from "@amzn/meridian/icon";
import Input from "@amzn/meridian/input";
import Loader from "@amzn/meridian/loader";
import Button from "@amzn/meridian/button";
import Divider from "@amzn/meridian/divider";
import RadioButton from "@amzn/meridian/radio-button";
import Card from "@amzn/meridian/card";
import LinearGauge from "@amzn/meridian/linear-gauge";
import arrowLeftTokens from "@amzn/meridian-tokens/base/icon/arrow-left-large";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "src/store/store";
import { WadmContainer, ContainerUsingFor, ItemFlowContext } from "src/types/containerTypes";
import { searchContainers } from "src/services/containerService";
import PillButton from "src/components/UI/PillButton";
import "src/components/pages/classification/classification.scss";
import "./toteItems.scss";

interface ChooseContainerLocationState {
  itemFlowContext: ItemFlowContext;
  wasteProfileIds: string[];
  donationCategoryIds: string[];
  usingFor: ContainerUsingFor;
}

interface ContainerCardProps {
  container: WadmContainer;
  isSelected: boolean;
  onSelect: (container: WadmContainer) => void;
}

// Container card component
// will extract this component to its own file if needed elsewhere.
const ContainerCard: React.FC<ContainerCardProps> = ({ container, isSelected, onSelect }) => {
  const currentWeight = container.usage?.weight?.weight ?? 0;
  const maxWeight = container.maxWeight?.weight ?? 0;
  const weightUnit = container.usage?.weight?.unit ?? container.maxWeight?.unit ?? "unknown";
  const openedOn = container.createdAt
    ? new Date(container.createdAt).toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "2-digit",
      })
    : "—";

  return (
    <div onClick={() => onSelect(container)}>
      <Card spacingInset="400">
        <Row alignmentVertical="top" spacing="300">
          <RadioButton
            checked={isSelected}
            onChange={() => onSelect(container)}
            value={container.id}
          />
          <Column spacing="100" width="fill">
            <Text type="b300" color="primary">
              {container.containerDisplayName ?? container.containerDisplayId ?? container.id}
            </Text>
            <Text type="b200" color="secondary">
              Opened on {openedOn}
            </Text>
            {container.containerDisplayId && (
              <Text type="b200" color="secondary">
                ID: {container.containerDisplayId}
              </Text>
            )}
            {container.customId1 && (
              <Text type="b200" color="secondary">
                Custom ID: {container.customId1}
              </Text>
            )}
            {maxWeight > 0 && (
              <Column spacing="300" spacingInset="300 none none">
                <LinearGauge
                  minValue={0}
                  maxValue={maxWeight}
                  value={currentWeight}
                  type="fill"
                  direction="horizontal"
                  fillColor="#059669"
                  trackWidth={5}
                />
                <Text type="b100" color="secondary">
                  {currentWeight} {weightUnit} / {maxWeight} {weightUnit}
                </Text>
              </Column>
            )}
          </Column>
        </Row>
      </Card>
    </div>
  );
};

// Choose container component.
const ChooseContainerPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as ChooseContainerLocationState | undefined;
  const { activeSiteCode } = useAppSelector((state) => state.user);

  const profileType =
    locationState?.usingFor === "DONATION" ? "donation category" : "waste profile";
  const OTHER_CONTAINERS_WARNING =
    `The containers below do not match the ${profileType} of this item. ` +
    `If selecting a container in this section, the item will inherit the ${profileType} of the selected container.`;

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedContainer, setSelectedContainer] = useState<WadmContainer | null>(null);
  const [recommendedContainers, setRecommendedContainers] = useState<WadmContainer[]>([]);
  const [otherContainers, setOtherContainers] = useState<WadmContainer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Fetch recommended and other containers on mount based on selected items' profile/category intersection
  useEffect(() => {
    if (!locationState) return;

    const fetchContainers = async () => {
      setIsLoading(true);
      try {
        const response = await searchContainers({
          siteName: activeSiteCode,
          wasteProfileIds: locationState.wasteProfileIds,
          donationCategoryIds: locationState.donationCategoryIds,
          usingFor: locationState.usingFor,
        });
        setRecommendedContainers(response.recommendedContainers);
        setOtherContainers(response.otherContainers);
      } catch (err) {
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContainers();
  }, [locationState]);

  // Filter recommended containers by search query
  const filteredRecommended = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return recommendedContainers;
    return recommendedContainers.filter(
      (c) =>
        c.containerDisplayName?.toLowerCase().includes(query) ||
        c.containerDisplayId?.toLowerCase().includes(query),
    );
  }, [searchQuery, recommendedContainers]);

  // Filter other containers by search query
  const filteredOther = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return otherContainers;
    return otherContainers.filter(
      (c) =>
        c.containerDisplayName?.toLowerCase().includes(query) ||
        c.containerDisplayId?.toLowerCase().includes(query),
    );
  }, [searchQuery, otherContainers]);

  // Navigate to confirm quantity page with selected container and items
  const handleChoose = () => {
    if (!selectedContainer || !locationState) return;
    navigate("/tote/confirmQuantity", {
      state: {
        itemFlowContext: locationState.itemFlowContext,
        container: selectedContainer,
        usingFor: locationState.usingFor,
      },
    });
  };

  if (!locationState) {
    return (
      <Column
        height="100vh"
        alignmentHorizontal="center"
        alignmentVertical="center"
        spacing="400"
        spacingInset="500"
      >
        <Text type="h200">No items selected</Text>
        <Text color="secondary">Please select items from a tote first.</Text>
        <PillButton text="Go back" color="secondary" onClick={() => navigate(-1)} />
      </Column>
    );
  }

  if (hasError) {
    return (
      <Column
        height="100vh"
        alignmentHorizontal="center"
        alignmentVertical="center"
        spacing="400"
        spacingInset="500"
      >
        <Text type="h200">Something went wrong</Text>
        <Text color="secondary">Could not load containers. Please go back and try again.</Text>
        <PillButton text="Go back" color="secondary" onClick={() => navigate(-1)} />
      </Column>
    );
  }

  return (
    <Column className="app-gradient-bg pwa-scrollable" spacing="none" spacingInset="none none 500">
      {/* Header */}
      <Row alignmentVertical="center" spacing="200" spacingInset="500 400 300">
        <Button type="icon" onClick={() => navigate(-1)}>
          <Icon color="success" tokens={arrowLeftTokens} />
        </Button>
        <Text type="h200" color="primary">
          Choose Container
        </Text>
      </Row>

      <Column spacing="400" spacingInset="none 500 600">
        <Text type="h300" color="primary">
          {locationState.usingFor === "DONATION"
            ? "Recommended Donation Containers"
            : "Recommended Waste Containers"}
        </Text>

        {/* Search / filter */}
        <Input
          type="text"
          placeholder="Search containers"
          value={searchQuery}
          onChange={(value: string) => setSearchQuery(value)}
        />

        {isLoading ? (
          <Column alignmentHorizontal="center" spacingInset="600 none">
            <Loader type="circular" size="large" />
          </Column>
        ) : (
          <>
            {filteredRecommended.length === 0 && filteredOther.length === 0 && (
              <Column alignmentHorizontal="center" spacingInset="600 none">
                <Text color="secondary">No containers available.</Text>
              </Column>
            )}

            {/* Recommended containers — match the waste profile / donation category intersection */}
            {filteredRecommended.map((container) => (
              <ContainerCard
                key={container.id}
                container={container}
                isSelected={selectedContainer?.id === container.id}
                onSelect={setSelectedContainer}
              />
            ))}

            {/* Other containers — don't match the profile; shown with a warning */}
            {filteredOther.length > 0 && (
              <>
                <Divider />
                <Text type="h300" color="primary">
                  Other available containers
                </Text>
                <Text type="b300" color="warning">
                  {OTHER_CONTAINERS_WARNING}
                </Text>

                {filteredOther.map((container) => (
                  <ContainerCard
                    key={container.id}
                    container={container}
                    isSelected={selectedContainer?.id === container.id}
                    onSelect={setSelectedContainer}
                  />
                ))}
              </>
            )}
          </>
        )}
      </Column>

      {/* Footer */}
      <Column className="asin-sticky-footer">
        <Button type="primary" size="large" disabled={!selectedContainer} onClick={handleChoose}>
          CHOOSE CONTAINER
        </Button>
      </Column>
    </Column>
  );
};

export default ChooseContainerPage;
